import { type EJobPriority, EJobStatus, type EJobType, type IJob } from '@quesmed/types/models';
import type { ReactNode } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { EDataView, EFormMode } from '@/config/globals';
import type { OmitFunctions } from '@/types/';
import { qsStorage } from '@/utils';
import { createSelectors } from './selector';

interface JobSlice {
  page: number;
  jobLoading: boolean;
  columns: string[];
  status: EJobStatus[];
  priority: EJobPriority | null;
  memberIds: number[];
  questionIds: number[];
  type: EJobType | null;
  search: string;
  view: EDataView;
  createModal: boolean;
  jobId: string;
  formMode: EFormMode;
  confirmModal: {
    isOpen: boolean;
    title: string;
    message: ReactNode;
    key: 'duplicate' | 'delete' | null;
    jobId: string | null;
  };
  currentJob: IJob | null;
  showDetails: boolean;
  totalJobs: number;

  reset: () => void;
  setColumns: (columns: string[]) => void;
  setCreateModal: (isOpen: boolean, formMode: EFormMode) => void;
  setConfirmModal: (
    isOpen: boolean,
    title: string,
    message: ReactNode,
    key: JobSlice['confirmModal']['key'],
    jobId: JobSlice['confirmModal']['jobId'],
  ) => void;
  setJobId: (jobId: string) => void;
  setStatus: (status: EJobStatus[]) => void;
  setPriority: (priority: EJobPriority | null) => void;
  setType: (type: EJobType | null) => void;
  setSearch: (search: string) => void;
  setView: (view: EDataView) => void;
  resetFilters: () => void;
  hasFilters: () => boolean;
  setPage: (page: number) => void;
  setCurrentJob: (job: IJob | null) => void;
  setJobLoading: (loading: boolean) => void;
  setShowDetails: (showDetails: boolean) => void;
  setMemberIds: (memberIds: number[]) => void;
  setTotalJobs: (totalJobs: number) => void;
  setQuestionIds: (questionIds: number[]) => void;
}

const initialState: OmitFunctions<JobSlice> = {
  createModal: false,
  currentJob: null,
  jobId: '',
  page: 1,
  jobLoading: false,
  memberIds: [],
  questionIds: [],
  columns: ['id', 'progress', 'type', 'status', 'actions'],
  status: [EJobStatus.ALL],
  priority: null,
  type: null,
  search: '',
  view: EDataView.GRID,
  formMode: EFormMode.CREATE,
  showDetails: true,
  totalJobs: 0,
  confirmModal: {
    isOpen: false,
    title: '',
    message: '',
    key: null,
    jobId: null,
  },
};

const jobSlice = create<JobSlice>()(
  persist(
    (set, get) => ({
      ...initialState,
      reset: () => {
        set(initialState);
      },
      setColumns: (columns) => set({ columns }),
      setCurrentJob: (job) => {
        set({ currentJob: job });
      },
      setCreateModal: (isOpen, formMode) => {
        set({ createModal: isOpen, formMode: !isOpen ? EFormMode.CREATE : formMode });
      },
      setConfirmModal: (isOpen, title, message, key, jobId) => {
        set({ confirmModal: { isOpen, title, message, key, jobId } });
      },
      setJobId: (jobId) => {
        set({ jobId });
      },
      hasFilters: () => {
        const { status, priority, type, search, page } = get();
        // Compare each field directly
        const isStatusEqual =
          Array.isArray(status) &&
          Array.isArray(initialState.status) &&
          status.length === initialState.status.length &&
          status.every((v, i) => v === initialState.status[i]);
        const isPriorityEqual = priority === initialState.priority;
        const isTypeEqual = type === initialState.type;
        const isSearchEqual = search === initialState.search;
        const isPageEqual = page === initialState.page;
        // Return true if any field is different from initial state
        return !(isStatusEqual && isPriorityEqual && isTypeEqual && isSearchEqual && isPageEqual);
      },
      setStatus: (status) => {
        set({ status, page: 1 });
      },
      setPriority: (priority) => {
        set({ priority, page: 1 });
      },
      setQuestionIds: (questionIds) => {
        set({ questionIds, page: 1 });
      },
      setMemberIds: (memberIds) => {
        set({ memberIds, page: 1 });
      },
      setType: (type) => {
        set({ type, page: 1 });
      },
      setSearch: (search) => {
        set({ search, page: 1 });
      },
      setPage: (page) => {
        set({ page });
      },
      resetFilters: () => {
        set({
          status: initialState.status,
          priority: initialState.priority,
          type: initialState.type,
          search: initialState.search,
          page: initialState.page,
          memberIds: initialState.memberIds,
          questionIds: initialState.questionIds,
        });
      },
      setView: (view) => {
        set({ view });
      },
      setJobLoading: (loading) => {
        set({ jobLoading: loading });
      },
      setShowDetails: (showDetails) => {
        set({ showDetails });
      },
      setTotalJobs: (totalJobs) => {
        set({ totalJobs });
      },
    }),
    {
      name: 'job-filters',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        status: state.status,
        priority: state.priority,
        type: state.type,
        search: state.search,
        view: state.view,
        page: state.page,
        memberIds: state.memberIds,
        questionIds: state.questionIds,
      }),
    },
  ),
);

export const useJob = createSelectors(jobSlice);
